// Главная — персональный хаб: состав и порядок блоков считает сервер (blocks_order). «Ещё» — серверный more_path,
// переведённый в экран приложения (routeFromApiPath); незнакомый адрес — без кнопки. Подписи блоков — block_titles.home
// → default (§2.9 п.1), иначе свои.
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlockTitle } from '../api/blockTitles';
import type { HomeScreen as HomeData } from '../api/homeTypes';
import { useConfig, useHome } from '../api/queries';
import { gameOff } from '../gameSwitch';
import { t } from '../i18n/i18n';
import { favoriteParams, useFavoritesState, type FavoritesState } from '../session/favorites';
import { paths, routeFromApiPath } from '../nav';
import { viewerRegion, videoAllowed } from '../region';
import { useVideoBlocks } from '../settings/localSettings';
import { Avatar } from '../ui/Avatar';
import { Card } from '../ui/Card';
import { FormStrip } from '../ui/FormStrip';
import { MatchListCard } from '../ui/MatchListCard';
import { RestoredRowsContext } from '../ui/MatchRowView';
import { NewsArticlesBlock } from '../ui/NewsCardView';
import { StandingsTable } from '../ui/StandingsTable';
import { EmptyStateView, ErrorView, Loading } from '../ui/States';
import { TeamLogo } from '../ui/TeamLogo';
import { VideoCard } from '../ui/VideoCard';
import { PredictionCard } from '../ui/PredictionCard';
import { WhoWinsPoll } from '../ui/WhoWinsPoll';
import { PickTeamsCard } from './pickTeams/PickTeamsCard';
import { rankCaption } from './homeModel';
import blocks from './match/Blocks.module.css';
import styles from './HomeScreen.module.css';

type Props = { data: HomeData };

function useMore() {
  const navigate = useNavigate();
  return (apiPath: string | undefined) => {
    const route = routeFromApiPath(apiPath);
    return route ? () => navigate(route.path, { state: route.tab ? { tab: route.tab } : undefined }) : undefined;
  };
}

function MyMatches({ data }: Props) {
  const more = useMore();
  const title = useBlockTitle('home');
  return <MatchListCard title={title('my_matches', t('mc_home_my_matches'))} matches={data.my_matches?.items ?? []} showLeague onOpenAll={more(data.my_matches?.more_path)} />;
}
function LiveNow({ data }: Props) {
  const more = useMore();
  const title = useBlockTitle('home');
  return <MatchListCard title={title('live_now', t('mc_live_now'))} matches={data.live_now?.items ?? []} showLeague onOpenAll={more(data.live_now?.more_path)} />;
}
function NextUp({ data }: Props) {
  const more = useMore();
  const title = useBlockTitle('home');
  return <MatchListCard title={title('next_up', t('mc_upcoming_matches'))} matches={data.next_up?.items ?? []} showLeague onOpenAll={more(data.next_up?.more_path)} />;
}

function News({ data }: Props) {
  const more = useMore();
  const title = useBlockTitle('home');
  const items = data.news?.items ?? [];
  if (!items.length) return null;
  return <Card title={title('news', t('mc_news'))} onOpen={more(data.news?.more_path)}><NewsArticlesBlock articles={items} /></Card>;
}

function Highlights({ data }: Props) {
  const more = useMore();
  const title = useBlockTitle('home');
  const region = viewerRegion();
  // видеоблоки выключены в профиле (HQ-1059) — карточки нет совсем, как в MediaBlocks
  const videoBlocks = useVideoBlocks();
  const items = (data.highlights?.items ?? []).filter((v) => videoAllowed(v.compliance?.region_restriction?.blocked, region));
  if (!videoBlocks || !items.length) return null;
  return (
    <Card title={title('highlights', t('mc_videos'))} onOpen={more(data.highlights?.more_path)}>
      <div className={styles.carousel}>{items.map((v) => <VideoCard key={v.id} item={v} />)}</div>
    </Card>
  );
}

function Standings({ data }: Props) {
  const navigate = useNavigate();
  const more = useMore();
  const title = useBlockTitle('home');
  return <>{(data.standings ?? []).filter((s) => s.rows.length).map((s) => (
    <Card key={s.league.id} title={s.league.name} subtitle={`${title('standings', t('mc_standings'))} · ${s.season.name}`} onOpen={more(s.more_path)} flush>
      <StandingsTable rows={s.rows} compact onTeam={(id) => navigate(paths.team(id))} />
    </Card>
  ))}</>;
}

function Leaders({ data }: Props) {
  const navigate = useNavigate();
  const more = useMore();
  const title = useBlockTitle('home');
  return <>{(data.leaders ?? []).map((l) => (
    <Card key={l.league.id} title={`${title('leaders', t('mc_leaders'))} · ${l.league.name}`} subtitle={l.season.name} onOpen={more(l.more_path)}>
      {([['runs', 'mc_stat_runs'], ['wickets', 'mc_stat_wickets']] as const).filter(([k]) => l[k]?.length).map(([k, label]) => (
        <div key={k} className={blocks.section}>
          <div className={blocks.sectionTitle}>{title(k, t(label))}</div>
          {l[k]!.map((r) => (
            <button key={r.player.id} className={blocks.impactRow} onClick={() => navigate(paths.player(r.player.id))}>
              <Avatar src={r.player.image} name={r.player.name} size={32} />
              <span className={blocks.impactText}><span className={blocks.personName}>{r.player.name}</span></span>
              <span className={`score ${blocks.impactValue}`}>{k === 'runs' ? r.runs : r.wickets}</span>
            </button>
          ))}
        </div>
      ))}
    </Card>
  ))}</>;
}

function WhoWins({ data }: Props) {
  const w = data.who_wins;
  const off = gameOff(useConfig().data);
  if (!w) return null;
  // игра выключена сервером (HQ-1572) — опрос болельщиков по самому блоку, запроса к игре нет
  if (off) return <WhoWinsPoll block={w} />;
  // старый опрос poll/ заменён игрой «Кто победит?» (§2.12); из блока берём только матч
  return <PredictionCard key={w.fixture_id} fixtureId={w.fixture_id} match={w.match} />;
}

function Rankings({ data }: Props) {
  const navigate = useNavigate();
  const more = useMore();
  const title = useBlockTitle('home');
  const tables = data.rankings?.tables ?? [];
  if (!tables.length) return null;
  return (
    <Card title={title('rankings', t('mc_team_ranking'))} onOpen={more(data.rankings?.more_path)}>
      {tables.map((table) => (
        <div key={`${table.format}-${table.gender}`} className={blocks.section}>
          <div className={blocks.sectionTitle}>{table.label} · {table.gender_label}</div>
          {table.teams.map((row) => (
            <button key={row.team.id} className={blocks.rankRow} style={{ width: '100%' }} onClick={() => navigate(paths.team(row.team.id))}>
              <span className="score" style={{ width: 22 }}>{row.position}</span>
              <TeamLogo src={row.team.image} code={row.team.code} />
              <span className={blocks.personName} style={{ textAlign: 'left' }}>{row.team.name}</span>
              <span className={`score ${blocks.muted}`}>{row.rating}</span>
            </button>
          ))}
        </div>
      ))}
    </Card>
  );
}

function Recommendations({ data }: Props) {
  const navigate = useNavigate();
  const title = useBlockTitle('home');
  const teams = data.recommendations ?? [];
  if (!teams.length) return null;
  return (
    <Card title={title('recommendations', t('mc_fav_recommendations'))}>
      <div className={styles.grid}>
        {teams.map((team) => (
          <button key={team.id} className={styles.teamCard} onClick={() => navigate(paths.team(team.id))}>
            <TeamLogo src={team.image} code={team.code} size={40} />
            <span className={styles.teamName}>{team.name}</span>
            {team.rank && <span className={blocks.caption}>{rankCaption(team.rank)}</span>}
            {team.form && team.form.length > 0 && <FormStrip form={team.form} />}
          </button>
        ))}
      </div>
    </Card>
  );
}

const BLOCKS: Record<string, ComponentType<Props>> = {
  my_matches: MyMatches, live_now: LiveNow, news: News, highlights: Highlights, next_up: NextUp,
  standings: Standings, leaders: Leaders, who_wins: WhoWins, rankings: Rankings, recommendations: Recommendations,
};

/** Ждём ли набор аккаунта: вошедший без пришедшего набора запрос главной не шлёт (HQ-1856) — пустые
 *  teams=&leagues=&players=&matches= сервер считает явным «избранного нет» и отдаёт главную без «Моих матчей».
 *  'ready' — запрос идёт; 'pending' — скелетон; 'offline' — набор на паузе без сети; 'failed' — набор с ошибкой.
 *  Кэш старта при ожидании рисуется как есть — эти состояния только для экрана без данных. */
export function homeFavoritesGate(f: Pick<FavoritesState, 'session' | 'loaded' | 'failed' | 'paused'>): 'ready' | 'pending' | 'offline' | 'failed' {
  if (!f.session || f.loaded) return 'ready';
  if (f.failed) return 'failed';
  return f.paused ? 'offline' : 'pending';
}

export function HomeScreen({ path }: { path: string }) {
  const favorites = useFavoritesState();
  const gate = homeFavoritesGate(favorites);
  // в кэш старта — только главная под окончательным набором избранного (не ответ «до набора»)
  const query = useHome(path, favoriteParams(favorites.set), { save: favorites.loaded || !favorites.session, enabled: gate === 'ready' });
  if (query.isPending) {
    // «Retry» набора — тот же запрос account/favorites/, что и в «Избранном»; пришёл набор — запрос главной включится
    if (gate === 'failed') return <ErrorView error={favorites.query.failureReason} onRetry={() => void favorites.query.refetch()} />;
    if (gate === 'offline') return <Loading query={favorites.query} />;
    if (gate === 'pending') return <Loading />;
    return <Loading query={query} />;
  }
  if (query.isError && !query.data) return <ErrorView error={query.error} onRetry={() => void query.refetch()} />;
  const data = query.data!;
  const known = data.blocks_order.filter((k) => BLOCKS[k]);
  if (!known.length && data.empty_state) {
    return <EmptyStateView state={data.empty_state as never} onReload={() => void query.refetch()} />;
  }
  return (
    <RestoredRowsContext.Provider value={query.restored}>
      <div className={styles.page}>
        {/* мягкая карточка выбора любимых (HQ-726): сессия есть, избранное пусто, «Пропустить» не жали 7 дней */}
        <PickTeamsCard />
        {known.map((key) => { const Block = BLOCKS[key]!; return <Block key={key} data={data} />; })}
      </div>
    </RestoredRowsContext.Provider>
  );
}
